import React, { useState } from 'react';
import { Plus, Save, Trash2, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { useLanguage } from '../../context/LanguageContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString('fr-FR', { maximumFractionDigits: 2 });

const TYPE_LABELS = {
    credit: { fr: 'Paiement manuel (crédit)', en: 'Manual payment (credit)', color: 'text-green-400 bg-green-500/10' },
    debit: { fr: 'Retrait manuel (débit)', en: 'Manual withdrawal (debit)', color: 'text-red-400 bg-red-500/10' },
    ajustement: { fr: 'Ajustement libre', en: 'Free adjustment', color: 'text-amber-400 bg-amber-500/10' },
};

export const ManualTxDialog = ({ open, onClose, provider, apps, auth, onSaved }) => {
    const { t, language } = useLanguage();
    const [type, setType] = useState('debit');
    const [appName, setAppName] = useState('');
    const [amount, setAmount] = useState('');
    const [impactReel, setImpactReel] = useState(false);
    const [deltaT, setDeltaT] = useState('');
    const [deltaR, setDeltaR] = useState('');
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);

    const save = async () => {
        if (type !== 'ajustement') {
            if (!appName) { toast.error(t('Choisissez une application', 'Select an application')); return; }
            if (!amount || parseFloat(amount) <= 0) { toast.error(t('Montant invalide', 'Invalid amount')); return; }
        }
        setSaving(true);
        try {
            await axios.post(`${API}/payments/apps/manual-transactions`, {
                provider: provider.id,
                type,
                app_name: type !== 'ajustement' ? appName : null,
                amount: type !== 'ajustement' ? parseFloat(amount) : null,
                impact_reel: type === 'debit' ? impactReel : false,
                delta_theorique: type === 'ajustement' ? (parseFloat(deltaT) || 0) : null,
                delta_reel: type === 'ajustement' ? (parseFloat(deltaR) || 0) : null,
                note: note || null,
            }, auth);
            toast.success(t('Transaction enregistrée', 'Transaction saved'));
            setAmount(''); setNote(''); setDeltaT(''); setDeltaR(''); setImpactReel(false);
            onSaved();
            onClose();
        } catch (e) { toast.error(e.response?.data?.detail || t('Erreur de sauvegarde', 'Save failed')); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="bg-slate-900 border-slate-700 text-white">
                <DialogHeader>
                    <DialogTitle>{t(`Transaction manuelle — ${provider.label}`, `Manual transaction — ${provider.label}`)}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                    <div>
                        <Label className="text-slate-300">Type</Label>
                        <Select value={type} onValueChange={setType}>
                            <SelectTrigger className="bg-slate-800 border-slate-600 text-white mt-1" data-testid={`manual-type-${provider.id}`}>
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent className="bg-slate-800 border-slate-600 text-white">
                                {Object.entries(TYPE_LABELS).map(([k, v]) => (
                                    <SelectItem key={k} value={k}>{language === 'fr' ? v.fr : v.en}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    {type !== 'ajustement' && (
                        <>
                            <div>
                                <Label className="text-slate-300">Application</Label>
                                <Select value={appName} onValueChange={setAppName}>
                                    <SelectTrigger className="bg-slate-800 border-slate-600 text-white mt-1" data-testid={`manual-app-${provider.id}`}>
                                        <SelectValue placeholder={t('Choisir...', 'Select...')} />
                                    </SelectTrigger>
                                    <SelectContent className="bg-slate-800 border-slate-600 text-white">
                                        {apps.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="text-slate-300">{t('Montant (XOF)', 'Amount (XOF)')}</Label>
                                <Input type="number" min="0" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
                                    className="bg-slate-800 border-slate-600 text-white mt-1" data-testid={`manual-amount-${provider.id}`} />
                            </div>
                            {type === 'credit' && (
                                <p className="text-xs text-slate-400">{t('Effet : solde théorique +montant • solde réel +commission% × montant', 'Effect: theoretical +amount • real +commission% × amount')}</p>
                            )}
                            {type === 'debit' && (
                                <>
                                    <div className="flex items-center gap-2">
                                        <Checkbox checked={impactReel} onCheckedChange={setImpactReel} id={`ir-${provider.id}`} data-testid={`manual-impact-reel-${provider.id}`} />
                                        <label htmlFor={`ir-${provider.id}`} className="text-sm text-slate-300 cursor-pointer">
                                            {t('Déduire aussi du solde réel (retrait de ma commission)', 'Also deduct from real balance (my commission withdrawal)')}
                                        </label>
                                    </div>
                                    <p className="text-xs text-slate-400">{t('Effet : solde théorique −montant', 'Effect: theoretical −amount')}{impactReel ? t(' • solde réel −montant', ' • real −amount') : ''}</p>
                                </>
                            )}
                        </>
                    )}
                    {type === 'ajustement' && (
                        <>
                            <div>
                                <Label className="text-slate-300">{t('Δ Solde théorique (XOF, ± possible)', 'Δ Theoretical balance (XOF, ± allowed)')}</Label>
                                <Input type="number" step="0.01" value={deltaT} onChange={(e) => setDeltaT(e.target.value)}
                                    className="bg-slate-800 border-slate-600 text-white mt-1" data-testid={`manual-delta-t-${provider.id}`} />
                            </div>
                            <div>
                                <Label className="text-slate-300">{t('Δ Solde réel (XOF, ± possible)', 'Δ Real balance (XOF, ± allowed)')}</Label>
                                <Input type="number" step="0.01" value={deltaR} onChange={(e) => setDeltaR(e.target.value)}
                                    className="bg-slate-800 border-slate-600 text-white mt-1" data-testid={`manual-delta-r-${provider.id}`} />
                            </div>
                        </>
                    )}
                    <div>
                        <Label className="text-slate-300">{t('Note (optionnel)', 'Note (optional)')}</Label>
                        <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('Ex: retrait agence Wave du 10/06', 'Ex: Wave agency withdrawal 06/10')}
                            className="bg-slate-800 border-slate-600 text-white mt-1" data-testid={`manual-note-${provider.id}`} />
                    </div>
                    <Button onClick={save} disabled={saving} className="w-full bg-blue-600 hover:bg-blue-700" data-testid={`manual-save-${provider.id}`}>
                        <Save size={14} className="mr-2" /> {t('Enregistrer', 'Save')}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export const ManualTxList = ({ transactions, provider, auth, onRefresh }) => {
    const { t, language } = useLanguage();
    const [open, setOpen] = useState(false);
    const [deleting, setDeleting] = useState(null);

    const remove = async (id) => {
        if (!window.confirm(t('Supprimer cette transaction manuelle ? Les soldes seront recalculés.', 'Delete this manual transaction? Balances will be recomputed.'))) return;
        setDeleting(id);
        try {
            await axios.delete(`${API}/payments/apps/manual-transactions/${id}`, auth);
            toast.success(t('Transaction supprimée', 'Transaction deleted'));
            onRefresh();
        } catch (e) { toast.error(t('Erreur de suppression', 'Delete failed')); }
        finally { setDeleting(null); }
    };

    if (!transactions || transactions.length === 0) return null;

    return (
        <div className="mt-3">
            <button onClick={() => setOpen(!open)} className="text-xs text-slate-400 hover:text-white flex items-center gap-1"
                data-testid={`manual-list-toggle-${provider.id}`}>
                {open ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                {t(`Transactions manuelles (${transactions.length})`, `Manual transactions (${transactions.length})`)}
            </button>
            {open && (
                <div className="mt-2 space-y-1">
                    {transactions.map((tx) => {
                        const lbl = TYPE_LABELS[tx.type] || TYPE_LABELS.ajustement;
                        return (
                            <div key={tx.id} className="flex items-center justify-between text-xs bg-slate-800/50 rounded px-3 py-2" data-testid={`manual-tx-${tx.id}`}>
                                <div className="flex items-center gap-2 flex-wrap">
                                    <span className={`px-2 py-0.5 rounded-full ${lbl.color}`}>{language === 'fr' ? lbl.fr : lbl.en}</span>
                                    {tx.app_name && <span className="text-white font-medium">{tx.app_name}</span>}
                                    {tx.type !== 'ajustement'
                                        ? <span className="text-slate-300">{fmt(tx.amount)} XOF{tx.type === 'debit' && tx.impact_reel ? t(' (réel inclus)', ' (real incl.)') : ''}</span>
                                        : <span className="text-slate-300">Δthéo {fmt(tx.delta_theorique)} / Δréel {fmt(tx.delta_reel)}</span>}
                                    {tx.note && <span className="text-slate-500 italic">« {tx.note} »</span>}
                                    <span className="text-slate-600">{new Date(tx.created_at).toLocaleDateString('fr-FR')}</span>
                                </div>
                                <Button size="sm" variant="ghost" onClick={() => remove(tx.id)} disabled={deleting === tx.id}
                                    className="h-6 w-6 p-0 text-red-400 hover:text-red-300" data-testid={`manual-delete-${tx.id}`}>
                                    <Trash2 size={12} />
                                </Button>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export const ManualTxButton = ({ onClick, provider }) => {
    const { t } = useLanguage();
    return (
        <Button size="sm" variant="outline" onClick={onClick}
            className="border-slate-600 text-slate-300" data-testid={`manual-tx-btn-${provider.id}`}>
            <Plus size={13} className="mr-1" /> {t('Transaction manuelle', 'Manual transaction')}
        </Button>
    );
};
